// Bounded reconnect policy for orphaned/interrupted chat streams (FRONT-773),
// unbounded while the turn is confirmed still live (FRONT-2195).
//
// A resume attempt (see resume-decision.ts + resume-pursuit-controller.ts) can itself
// fail — a network error or a non-2xx response while re-attaching to the
// stream. That is NOT the same as a clean resume: when the server replays
// the recorded frames and cleanly ends, that's a success even if the
// underlying turn was interrupted. Only a failed resume *request* counts
// against the retry budget here.
//
// Policy: at most 3 total attempts per streamId, with backoff before each
// retry (2s before attempt 2, 6s before attempt 3). After the 3rd attempt
// fails, that chat instance has permanently given up on the streamId for as
// long as it stays mounted — its records are released when it unmounts, so a
// reopened chat gets a fresh budget. **Unless** the caller passes
// `turnLive: true` (FRONT-2195, from the latest `/history` snapshot's
// `streamLive` flag for this streamId) — in that case the streamId never
// gives up: attempts 2 and 3 keep the same 2s/6s backoff, and every attempt
// after that retries on a steady 15s cadence for as long as the server keeps
// reporting the turn as live. `turnLive` defaults to `false`, so a caller
// that never passes it gets exactly the pre-FRONT-2195 behaviour.
//
// A resume response the server REFUSED — any 4xx (the row is gone, it is not
// this person's, the login lapsed) — is terminal regardless of `turnLive`
// (FRONT-2195 review F4): retrying a refusal every 15s forever changes
// nothing, and only a give-up restores the partial the pursuit stripped.
//
// `decideResumeRetry` is a pure function over injected state + `now` — no
// timers, no I/O — so it's unit-testable in isolation. The registry below is
// the module-scoped bookkeeping, keyed per (hook instance, streamId) — see
// its own comment for why per instance.

export const MAX_RESUME_ATTEMPTS = 3;

// Delay (ms) before firing the Nth attempt, indexed by attempts-already-made.
// Attempt 1 fires immediately (driven directly by the resume trigger, not
// this policy). After attempt 1 fails, wait 2s before attempt 2; after
// attempt 2 fails, wait 6s before attempt 3.
const BACKOFF_MS: readonly number[] = [0, 2000, 6000];

// FRONT-2195: once a live turn has exhausted the same 2s/6s ramp above, every
// further attempt waits this long — frequent enough to reconnect promptly,
// infrequent enough not to hammer the resume endpoint for a turn that can
// legitimately run for tens of minutes.
const LIVE_STEADY_BACKOFF_MS = 15_000;

export interface ResumeRetryState {
  /** Attempts already fired for this streamId. */
  attempts: number;
  /** Epoch ms of the most recent attempt, or null before the first. */
  lastAttemptAt: number | null;
}

export interface ResumeRetryOptions {
  /**
   * FRONT-2195: the latest `/history` snapshot says this streamId's turn is
   * still running server-side right now. When true, the retry budget never
   * runs out — see the module docblock. Defaults to `false` (the pre-
   * FRONT-2195 bounded policy).
   */
  turnLive?: boolean;
  /**
   * FRONT-2195 review F4: the HTTP status of the failed resume response, or
   * null/absent when there was none (a network error, a mid-stream drop).
   * Any 4xx is terminal — see `isTerminalResumeStatus`.
   */
  httpStatus?: number | null;
}

/**
 * A resume response the server refused outright: every 4xx. Retrying one
 * cannot succeed (a deleted row stays deleted, a lapsed login stays lapsed),
 * so it ends the pursuit even while the turn reads as live. A 5xx, a network
 * error and a mid-stream drop (no status at all) stay retryable.
 */
export function isTerminalResumeStatus(status: number | null | undefined): boolean {
  return typeof status === 'number' && status >= 400 && status < 500;
}

/**
 * The status the retry decision should see for a failed resume, given
 * whether the `/history` refetch that follows every failure succeeded. A 401
 * is usually just the hourly access-token lapse: the resume request goes
 * out through the AI SDK's own fetch, which has no silent renewal, while the
 * `/history` refetch goes through the api client, which renews and retries.
 * So a 401 followed by a refetch that WORKED means the session is fine again
 * — retry (no status). A 401 whose refetch also failed stays terminal, as
 * does every other 4xx.
 */
export function effectiveResumeStatus(
  httpStatus: number | null | undefined,
  historyRefetched: boolean,
): number | null {
  if (httpStatus == null) return null;
  if (httpStatus === 401 && historyRefetched) return null;
  return httpStatus;
}

export interface ResumeRetryDecision {
  /** Whether another attempt should ever be scheduled. */
  shouldAttempt: boolean;
  /** Milliseconds to wait (from `now`) before firing the next attempt. */
  delayMs: number;
  /** True once the budget is exhausted — permanent for this chat instance while it stays mounted. */
  gaveUp: boolean;
}

/**
 * Pure decision over injected state + `now`. The caller owns scheduling
 * (setTimeout) and bookkeeping — this function only computes what should
 * happen next.
 */
export function decideResumeRetry(
  state: ResumeRetryState,
  now: number,
  opts: ResumeRetryOptions = {},
): ResumeRetryDecision {
  const turnLive = opts.turnLive ?? false;
  if (isTerminalResumeStatus(opts.httpStatus)) {
    return { shouldAttempt: false, delayMs: 0, gaveUp: true };
  }
  if (!turnLive && state.attempts >= MAX_RESUME_ATTEMPTS) {
    return { shouldAttempt: false, delayMs: 0, gaveUp: true };
  }
  let fullDelay = 0;
  if (state.attempts < BACKOFF_MS.length) {
    fullDelay = BACKOFF_MS[state.attempts];
  } else if (turnLive) {
    fullDelay = LIVE_STEADY_BACKOFF_MS;
  }
  // (A non-live pursuit past the table has already given up above, so the
  // 0 only matters if BACKOFF_MS ever grows past MAX_RESUME_ATTEMPTS.)
  const elapsed = state.lastAttemptAt != null ? Math.max(0, now - state.lastAttemptAt) : 0;
  const delayMs = Math.max(0, fullDelay - elapsed);
  return { shouldAttempt: true, delayMs, gaveUp: false };
}

// ── Module-level registry ──────────────────────────────────────────────────
// Keyed by (owner, streamId): `owner` is one `useAgentChat` instance's id
// (resume-pursuit-controller.ts). Per INSTANCE, not per streamId (FRONT-2195
// review F6): two surfaces can show the same session at once — the chat
// window and the operator panel's Chat tab — and each renders its OWN AI SDK
// chat, so each needs its own live connection. Keyed by streamId alone, the
// first instance to arm owned the turn for the whole tab and the other sat on
// a frozen snapshot.
//
// Module scope (not a component ref) only so `message-bubble.tsx` can read
// the give-up set without a provider; an instance releases every record it
// owns when it unmounts (`releaseResumeRecordsForOwner`), so nothing
// survives a remount. A full page reload clears it as well.

interface ResumeAttemptRecord {
  owner: string;
  streamId: string;
  attempts: number;
  lastAttemptAt: number | null;
  gaveUp: boolean;
}

const registry = new Map<string, ResumeAttemptRecord>();

function registryKey(owner: string, streamId: string): string {
  return `${owner}\u0000${streamId}`;
}

function getOrCreateRecord(owner: string, streamId: string): ResumeAttemptRecord {
  const key = registryKey(owner, streamId);
  let rec = registry.get(key);
  if (!rec) {
    rec = { owner, streamId, attempts: 0, lastAttemptAt: null, gaveUp: false };
    registry.set(key, rec);
  }
  return rec;
}

/** True if ANY instance has permanently given up on this streamId. */
export function hasGivenUp(streamId: string): boolean {
  for (const rec of registry.values()) {
    if (rec.streamId === streamId && rec.gaveUp) return true;
  }
  return false;
}

/**
 * True if THIS owner already has bookkeeping for the streamId — an attempt
 * has fired, is scheduled, or it has given up. Lets the owner's detection
 * pass recognise its own in-progress or terminal pursuit instead of starting
 * a duplicate one; another owner's record never counts. Does not create an
 * entry (unlike the record* functions).
 */
export function hasResumeRecord(owner: string, streamId: string): boolean {
  return registry.has(registryKey(owner, streamId));
}

/**
 * Drop every record `owner` holds — called when its hook instance unmounts
 * or switches session, so no record outlives the instance that could act on
 * it (FRONT-2195 review F4: a leftover record blocked the next instance from
 * ever attaching). Notifies give-up subscribers when the set changes.
 */
export function releaseResumeRecordsForOwner(owner: string): void {
  for (const [key, rec] of registry) {
    if (rec.owner === owner) registry.delete(key);
  }
  if (refreshGaveUpSnapshot()) notifyGaveUpListeners();
}

function computeGaveUpSnapshot(): ReadonlySet<string> {
  const out = new Set<string>();
  for (const rec of registry.values()) {
    if (rec.gaveUp) out.add(rec.streamId);
  }
  return out;
}

function gaveUpSetsEqual(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/** Snapshot of every streamId some mounted chat instance has given up on. */
export function getGaveUpStreamIds(): ReadonlySet<string> {
  return computeGaveUpSnapshot();
}

// ── Subscription surface (FRONT-773 defect 1) ──────────────────────────────
// The registry above is the single source of truth for give-up state — it
// must NOT be shadowed by per-`useAgentChat`-instance React state, because
// the inbox chat modal renders MessageBubble via its OWN useAgentChat
// instance with no ChatProvider ancestor: a bubble reading React state would
// resolve the unrelated global provider's state instead of this instance's.
// useSyncExternalStore lets any component read this module singleton
// directly, so global-chat and modal-chat bubbles alike see the same truth.
//
// `getGaveUpSnapshot` returns a CACHED ReadonlySet reference, replaced only
// when the set's contents actually change — useSyncExternalStore compares
// snapshots by reference and loops (or warns) if a fresh object comes back
// on every read even when nothing changed.

type GaveUpListener = () => void;

const gaveUpListeners = new Set<GaveUpListener>();
let gaveUpSnapshot: ReadonlySet<string> = new Set();

/** Recomputes the cached snapshot; returns true if it changed. */
function refreshGaveUpSnapshot(): boolean {
  const next = computeGaveUpSnapshot();
  if (gaveUpSetsEqual(gaveUpSnapshot, next)) return false;
  gaveUpSnapshot = next;
  return true;
}

function notifyGaveUpListeners(): void {
  for (const listener of gaveUpListeners) listener();
}

/**
 * Subscribe to give-up transitions. Compatible with useSyncExternalStore's
 * `subscribe` parameter: takes a no-arg callback, returns an unsubscribe fn.
 */
export function subscribeGaveUpStreamIds(listener: GaveUpListener): () => void {
  gaveUpListeners.add(listener);
  return () => {
    gaveUpListeners.delete(listener);
  };
}

/**
 * Cached snapshot for useSyncExternalStore's `getSnapshot` parameter. Same
 * reference across calls until the give-up set actually changes.
 */
export function getGaveUpSnapshot(): ReadonlySet<string> {
  return gaveUpSnapshot;
}

/** Record that an attempt is being fired now (call right before invoking resumeStream()). */
export function recordResumeAttempt(owner: string, streamId: string, now: number): void {
  const rec = getOrCreateRecord(owner, streamId);
  rec.attempts += 1;
  rec.lastAttemptAt = now;
}

/**
 * Record a failed attempt and decide what happens next. Returns the same
 * shape as `decideResumeRetry` so the caller can schedule the next attempt
 * or treat this as the terminal give-up. `opts.turnLive` (FRONT-2195) is
 * passed straight through to `decideResumeRetry` — see its docblock.
 */
export function recordResumeFailure(
  owner: string,
  streamId: string,
  now: number,
  opts: ResumeRetryOptions = {},
): ResumeRetryDecision {
  const rec = getOrCreateRecord(owner, streamId);
  const decision = decideResumeRetry(rec, now, opts);
  if (decision.gaveUp) {
    rec.gaveUp = true;
    if (refreshGaveUpSnapshot()) notifyGaveUpListeners();
  }
  return decision;
}

/**
 * A resume that completed cleanly (full replay-to-end or a live tail that
 * finished) clears bookkeeping entirely — it is a success, not an error, so
 * nothing about this streamId should linger in the registry.
 */
export function recordResumeSuccess(owner: string, streamId: string): void {
  registry.delete(registryKey(owner, streamId));
  if (refreshGaveUpSnapshot()) notifyGaveUpListeners();
}

/** Test-only: reset all bookkeeping between test cases. */
export function __resetResumeRegistryForTests(): void {
  registry.clear();
  gaveUpSnapshot = new Set();
}
